/**
 * Store discount coupons: one per order, off the products (never shipping). A coupon can leave out
 * product categories and specific products; the minimum is checked on the eligible products at
 * bank-transfer prices. Created and switched on/off in Admin › Cupones.
 */

export const COUPON_KINDS = ["percent", "fixed"] as const;
export type CouponKind = (typeof COUPON_KINDS)[number];

export const COUPON_CODE_RE = /^[A-Z0-9_-]{3,24}$/;
export const normalizeCode = (raw: unknown): string => (typeof raw === "string" ? raw.trim().toUpperCase().replace(/\s+/g, "") : "");

export interface CouponInput {
  code: string;
  description: string;
  kind: CouponKind;
  /** percent: 1–90; fixed: whole pesos off. */
  value: number;
  /** Minimum of the eligible products (pesos, transfer prices); null = none. */
  minArs: number | null;
  /** Total uses across customers; null = unlimited. */
  maxUses: number | null;
  /** Uses per customer account (default 1). */
  perUser: number;
  startsAt: string | null;
  endsAt: string | null;
  excludedCategories: string[];
  excludedProductIds: number[];
  active: boolean;
}

export interface CouponView extends CouponInput {
  id: number;
  /** Paid orders that used it. */
  used: number;
  /** Unpaid orders holding it right now. */
  held: number;
  /** Names of the excluded products, for the admin. */
  excludedProducts: { id: number; name: string }[];
  createdAt: string;
}

/** What the checkout shows after "Aplicar". */
export interface CouponQuote {
  code: string;
  description: string;
  kind: CouponKind;
  value: number;
  /** Lines (variant ids) the coupon does not apply to. */
  excludedVariantIds: number[];
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const int = (v: unknown) => (typeof v === "number" ? v : typeof v === "string" && v.trim() !== "" ? Number(v) : NaN);

export function parseCouponInput(b: Record<string, unknown>): { ok: true; value: CouponInput } | { ok: false; errors: string[] } {
  const errors: string[] = [];
  const code = normalizeCode(b.code);
  if (!COUPON_CODE_RE.test(code)) errors.push("code");
  const kind = b.kind === "fixed" ? "fixed" : b.kind === "percent" ? "percent" : null;
  if (!kind) errors.push("kind");
  const value = int(b.value);
  if (!Number.isInteger(value) || value < 1 || (kind === "percent" ? value > 90 : value > 100_000_000)) errors.push("value");
  const opt = (v: unknown, min: number) => {
    if (v === null || v === undefined || v === "") return null;
    const n = int(v);
    if (!Number.isInteger(n) || n < min) {
      errors.push("limits");
      return null;
    }
    return n;
  };
  const minArs = opt(b.minArs, 1);
  const maxUses = opt(b.maxUses, 1);
  const perUser = opt(b.perUser, 1) ?? 1;
  const day = (v: unknown, key: string) => {
    if (v === null || v === undefined || v === "") return null;
    if (typeof v === "string" && DAY.test(v)) return v;
    errors.push(key);
    return null;
  };
  const startsAt = day(b.startsAt, "startsAt");
  const endsAt = day(b.endsAt, "endsAt");
  if (startsAt && endsAt && endsAt < startsAt) errors.push("endsAt");
  const cats = Array.isArray(b.excludedCategories) ? b.excludedCategories : [];
  const ids = Array.isArray(b.excludedProductIds) ? b.excludedProductIds : [];
  const excludedCategories = [...new Set(cats.filter((c): c is string => typeof c === "string" && /^[a-z0-9-]{1,40}$/.test(c)))].slice(0, 20);
  const excludedProductIds = [...new Set(ids.map(Number).filter((n) => Number.isInteger(n) && n > 0))].slice(0, 500);
  if (errors.length) return { ok: false, errors: [...new Set(errors)] };
  return {
    ok: true,
    value: {
      code,
      description: typeof b.description === "string" ? b.description.trim().slice(0, 200) : "",
      kind: kind!,
      value,
      minArs,
      maxUses,
      perUser,
      startsAt,
      endsAt,
      excludedCategories,
      excludedProductIds,
      active: b.active !== false,
    },
  };
}

/** Whether the coupon covers a product. */
export function couponCovers(c: Pick<CouponInput, "excludedCategories" | "excludedProductIds">, p: { productId: number; category: string }): boolean {
  return !c.excludedCategories.includes(p.category) && !c.excludedProductIds.includes(p.productId);
}

/** "15% off" / "$5.000 off" text pieces for the UI. */
export function couponAmount(c: Pick<CouponInput, "kind" | "value">, formatArs: (n: number) => string): string {
  return c.kind === "percent" ? `${c.value}%` : formatArs(c.value);
}
